import { useEffect, useRef, useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { PageHeader } from '../../components/PageHeader'
import { uploadProfileAvatar } from '../../lib/api/profile'
import { looksLikeHeic } from '../../lib/heic'
import { AvatarCropOverlay } from '../../components/AvatarCropOverlay'
import { useAuth } from '../../lib/auth'
import { readError } from '../../lib/errors'
import { supabase } from '../../lib/supabase'
import type { Profile } from '../types'
import { useT } from '../../i18n'

interface Props {
  profile: Profile
  onBack: () => void
}

const MAX_NAME = 24
const USERNAME_RE = /^[a-z][a-z0-9_]{2,23}$/

/**
 * Edit profile — change username, display name, upload an avatar.
 * Username is validated locally for format and pinged against
 * `username_available` for uniqueness; display_name and the avatar
 * have their existing flows.
 */
export function EditProfileScreen({ profile, onBack }: Props) {
  const t = useT()
  const { updateProfile, session } = useAuth()
  const email = session?.user?.email ?? null
  const [username, setUsername] = useState(profile.username)
  const [name, setName] = useState(profile.display_name)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile.avatar_url)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // The raw picked file while the crop overlay is open. The cropped
  // result is what lands in pendingFile.
  const [cropSource, setCropSource] = useState<File | null>(null)

  // Live username availability check (debounced).
  type Status = 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [usernameStatus, setUsernameStatus] = useState<Status>('idle')

  useEffect(() => {
    const cleaned = username.trim().toLowerCase()
    if (cleaned === profile.username) { setUsernameStatus('idle'); return }
    if (!USERNAME_RE.test(cleaned))   { setUsernameStatus('invalid'); return }
    setUsernameStatus('checking')
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const { data, error: e } = await supabase.rpc('username_available', { p_username: cleaned })
        if (cancelled) return
        if (e) { setUsernameStatus('invalid'); return }
        setUsernameStatus(data ? 'available' : 'taken')
      } catch {
        if (!cancelled) setUsernameStatus('invalid')
      }
    }, 350)
    return () => { cancelled = true; clearTimeout(t) }
  }, [username, profile.username])

  const trimmedName = name.trim()
  const cleanedUsername = username.trim().toLowerCase()
  const usernameDirty = cleanedUsername !== profile.username
  const nameDirty = trimmedName.length > 0 && trimmedName !== profile.display_name
  const photoDirty = pendingFile !== null
  const dirty = usernameDirty || nameDirty || photoDirty

  // Save is gated on (a) something changed, and (b) if the username
  // changed it must have passed the availability + format check.
  const usernameOk = !usernameDirty || usernameStatus === 'available'
  const ready = dirty && !busy && trimmedName.length > 0 && usernameOk

  async function handleFile(file: File) {
    setError(null)
    // Desktop pickers hand over HEIC with an empty type often enough
    // that the MIME check alone would reject a normal iPhone photo.
    if (!file.type.startsWith('image/') && !(await looksLikeHeic(file))) {
      setError(t('That file does not look like an image.'))
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(t('Pick an image under 10 MB.'))
      return
    }
    // Don't accept the file yet: open the crop overlay so the user
    // frames the circle themselves instead of getting an automatic
    // centre-crop they never saw.
    setCropSource(file)
  }

  async function handleSave() {
    if (!ready) return
    setBusy(true); setError(null)
    try {
      // Username goes through its own RPC because it has stricter
      // server-side validation than a direct profiles update.
      if (usernameDirty) {
        const { error: e } = await supabase.rpc('set_username', { p_username: cleanedUsername })
        if (e) throw e
      }
      const patch: Partial<Pick<Profile, 'display_name' | 'avatar_url' | 'username'>> = {}
      if (usernameDirty) patch.username = cleanedUsername
      if (nameDirty) patch.display_name = trimmedName
      if (pendingFile) {
        patch.avatar_url = await uploadProfileAvatar(profile.id, pendingFile)
      }
      await updateProfile(patch)
      setPendingFile(null)
      setSavedAt(Date.now())
    } catch (e) {
      setError(readError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="screen pb-32">
      <PageHeader title={t('Edit profile')} onBack={onBack} />

      <div className="px-5 py-6 space-y-8">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="relative active:scale-95 transition-transform
                       disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label={t('Change profile photo')}
          >
            <Avatar name={trimmedName || profile.display_name} avatarUrl={avatarPreview} size="xl" />
            <span className="absolute -bottom-0.5 -right-0.5 w-8 h-8 rounded-full
                             bg-brand-text text-white text-sm flex items-center
                             justify-center shadow-card border-2 border-white">
              ✎
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={e => {
              const f = e.target.files?.[0]
              if (f) void handleFile(f)
              if (e.target) e.target.value = ''
            }}
          />
          <p className="text-xs text-brand-muted text-center max-w-xs">{t('Tap the photo to choose a new one. You can zoom and position it before it saves. Anything up to 10 MB is fine.')}</p>
        </div>

        {/* Username */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="username"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('Username')}
            </label>
            <UsernameStatusPill status={usernameStatus} dirty={usernameDirty} />
          </div>
          <input
            id="username"
            type="text"
            value={username}
            onChange={e => setUsername(
              e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 24)
            )}
            placeholder={t('jodan')}
            maxLength={24}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="username"
            className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                       text-brand-text placeholder:text-brand-muted
                       focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                       focus:ring-brand-lime/30 transition-all"
          />
          <p className="text-[11px] text-brand-muted mt-1">
            {t('3–24 chars · lowercase letters, digits, underscore · starts with a letter.')}
          </p>
        </div>

        {/* Display name */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="display_name"
                   className="text-xs font-medium text-brand-muted uppercase tracking-wide">
              {t('Display name')}
            </label>
            <span className={`text-xs font-medium
              ${name.length > MAX_NAME * 0.85
                ? 'text-brand-danger' : 'text-brand-muted'}`}>
              {name.length}/{MAX_NAME}
            </span>
          </div>
          <input
            id="display_name"
            type="text"
            value={name}
            onChange={e => setName(e.target.value.slice(0, MAX_NAME))}
            placeholder={t('Your name')}
            maxLength={MAX_NAME}
            autoComplete="nickname"
            className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-white
                       text-brand-text placeholder:text-brand-muted
                       focus:outline-hidden focus:border-brand-lime-dark focus:ring-2
                       focus:ring-brand-lime/30 transition-all"
          />
          <p className="text-[11px] text-brand-muted mt-1">
            {t('Free-form name shown at game seats. Doesn’t have to be unique.')}
          </p>
        </div>

        {/* The account behind the profile. Read-only: email changes run
            through auth flows, not this form. Apple Hide My Email users
            correctly see their relay address here, since that is the
            address the account signs in with. */}
        {email && (
          <div>
            <label className="text-xs font-medium text-brand-muted uppercase tracking-wide block mb-2">
              {t('Signed in as')}
            </label>
            <div className="w-full px-4 py-3 rounded-2xl border border-brand-border bg-brand-elevated
                            text-brand-text text-sm break-all select-all">
              {email}
            </div>
          </div>
        )}

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {savedAt && !dirty && !error && (
          <p className="text-sm text-iet-green text-center">{t('Saved ✓')}</p>
        )}
      </div>

      <div className="floating-actions">
        <button onClick={handleSave} disabled={!ready} className="btn-primary">
          {busy ? 'Saving…' : t('Save changes')}
        </button>
      </div>

      {cropSource && (
        <AvatarCropOverlay
          file={cropSource}
          onCancel={() => setCropSource(null)}
          onConfirm={cropped => {
            setPendingFile(cropped)
            setAvatarPreview(URL.createObjectURL(cropped))
            setCropSource(null)
          }}
        />
      )}
    </div>
  )
}

function UsernameStatusPill({
  status, dirty,
}: {
  status: 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  dirty: boolean
}) {
  if (!dirty || status === 'idle') return null
  const map = {
    checking:  { label: 'Checking…', cls: 'text-brand-muted' },
    available: { label: 'Available', cls: 'text-iet-green' },
    taken:     { label: 'Taken',     cls: 'text-brand-danger' },
    invalid:   { label: 'Invalid',   cls: 'text-brand-danger' },
  }
  const { label, cls } = map[status]
  return <span className={`text-xs font-semibold ${cls}`}>{label}</span>
}
